import Calc from 'com.squareup.calc.Calc';
import UUID from 'uuid-js';
import { isNone } from '@ember/utils';
import TokenPair from '@square/dashboard-data/models/squareup/common/token/token-pair';
import { ApplicationScope, CalculationPhase } from '@square/dashboard-items-data/models/invoices/enums';
import Money from '@square/dashboard-money/models/money';
import Cart from '@square/dashboard-items-data/models/invoices/cart/cart';
import Itemization from '@square/dashboard-items-data/models/invoices/cart/cart-itemization';
import DiscountLineItem from '@square/dashboard-items-data/models/invoices/cart/discount-line-item';
import { ModifyTaxBasis } from '@square/dashboard-items-data/models/invoices/cart/discount-snapshot';
import FeeLineItem from '@square/dashboard-items-data/models/invoices/cart/fee-line-item';
import SurchargeLineItem from '@square/dashboard-items-data/models/invoices/cart/surcharge-line-item';
import { calculateAfterApplicationItemizationsTotalAmount, calculateAppliedToAmount, getSurchargeId, isApportionedSurcharge } from '@square/dashboard-items-data/utils/invoices/cart-calculator';
import { datadogRum } from '@datadog/browser-rum';
const {
  TaxAndDiscountAdjuster,
  CalculationHelper
} = Calc;
class CalcAdjusterConfig {
  disableNewBigDecimalCosmetics() {
    return true;
  }
  enableCalculateSurchargesEarlyExit() {
    return true;
  }
  bigDecimalCacheEnabled() {
    return false;
  }
  enableReducedDecimalPrecision() {
    return true;
  }
  enableFlatAmountTaxes() {
    return false;
  }
  enableCalculateCardSurchargeAfterTaxes() {
    return false;
  }
}
class CalcClock {
  elapsedRealtime() {
    return 0;
  }
}
class CalcDiscount {
  constructor(discountLineItem) {
    const discount = discountLineItem.get('writeOnlyBackingDetails.discount');
    const {
      applicationScope,
      configuration
    } = discountLineItem;
    this._id = discount.get('id');
    this._taxBasis = ModifyTaxBasis[discount.get('modifyTaxBasis')];
    this._quantity = discountLineItem.get('discountQuantity');
    this._maxAmount = discount.get('maximumAmount.amount');
    const configVariableAmount = configuration?.get('variableAmountMoney.amount');
    const configVariablePercentage = configuration?.get('variablePercentage');
    switch (discount.get('discountType')) {
      case 'FIXED':
        if (!isNone(discount.get('percentage'))) {
          this._phase = 'DISCOUNT_PERCENTAGE_PHASE';
          this._rate = discount.get('percentage') || 0;
          this._amount = null;
        } else {
          this._phase = 'DISCOUNT_AMOUNT_PHASE';
          this._rate = null;
          this._amount = discount.get('amount.amount') || 0;
        }
        break;
      case 'VARIABLE_AMOUNT':
        this._phase = 'DISCOUNT_AMOUNT_PHASE';
        this._rate = null;
        this._amount = configVariableAmount || 0;
        break;
      case 'VARIABLE_PERCENTAGE':
        this._phase = 'DISCOUNT_PERCENTAGE_PHASE';
        this._rate = configVariablePercentage || 0;
        this._amount = null;
        break;
      default:
        throw new Error(`Unrecognized discount_type for ${JSON.stringify(discount)}`);
    }
    switch (applicationScope) {
      case ApplicationScope.ITEMIZATION_LEVEL:
        this._applicationScope = 'LINE_ITEM';
        break;
      case ApplicationScope.ITEMIZATION_LEVEL_PER_QUANTITY:
        this._applicationScope = 'LINE_ITEM_PER_QUANTITY';
        break;
      default:
        this._applicationScope = 'ORDER';
    }

    // When a discount has both a percentage and a maximum amount set, the maximum amount should be a
    // cap for each individual discount application. To achieve this behavior, we will set `amount()`
    // to the maximum amount value. The `maxAmount()` method is used in a different scenario, when we
    // want to cap the total value of a flat amount discount that may be applied multiple times.
    this._isPercentageMaxAmountDiscount = this._rate && this._maxAmount;
  }
  id() {
    return this._id;
  }
  inclusionType() {
    // We expect the calculator won't care about the inclusionType of Discounts.
    return 'ADDITIVE';
  }
  taxBasis() {
    return this._taxBasis;
  }
  phase() {
    return this._phase;
  }
  rate() {
    if (!isNone(this._rate)) {
      return String(this._rate);
    }
    return undefined;
  }
  amount() {
    if (this._isPercentageMaxAmountDiscount) {
      return this._maxAmount;
    }
    return this._amount;
  }
  applicationScope() {
    return this._applicationScope;
  }
  maxAmount() {
    if (this._isPercentageMaxAmountDiscount) {
      return undefined;
    }
    return this._maxAmount;
  }
  quantity() {
    return this._quantity;
  }
  adjustmentType() {
    return 'SUBTRACTIVE';
  }
}
class CalcFee {
  constructor(fee) {
    this.fee = fee;
  }
  id() {
    return this.fee.get('id');
  }
  inclusionType() {
    return this.fee.get('inclusionType') || 'ADDITIVE'; // See REGSERVER-1403 for context.
  }
  phase() {
    return this.fee.get('calculationPhase') || 'FEE_SUBTOTAL_PHASE';
  }
  rate() {
    return String(this.fee.get('percentage') || '0');
  }
  amount() {
    return undefined; // NOTE(matthewtodd): We don't have amount-based fees.
  }
  applicationScope() {
    return 'ORDER';
  }
  maxAmount() {
    return undefined;
  }
  quantity() {
    return undefined;
  }
  adjustmentType() {
    return 'ADDITIVE';
  }
}
class CalcSurcharge {
  constructor(surchargeLineItem) {
    this.surchargeLineItem = surchargeLineItem;
    this.surcharge = surchargeLineItem.backingDetails?.surcharge;
    this.fees = surchargeLineItem.fees || [];
    this._percentage = this.surcharge.percentage;
    this._amount = this.surcharge.amount?.cents;
    // Based off android implementation: https://github.com/squareup/android-register/blob/master/common/checkout-hairball/src/main/java/com/squareup/checkout/Surcharge.kt#L97-L101
    this._taxBasis = this.surcharge.taxable ? ModifyTaxBasis.MODIFY_TAX_BASIS : ModifyTaxBasis.DO_NOT_MODIFY_TAX_BASIS;
    // Based off android implementation: https://github.com/squareup/android-register/blob/master/common/checkout-hairball/src/main/java/com/squareup/checkout/Surcharge.kt
    switch (this.surchargeLineItem.applicationScope) {
      case ApplicationScope.ITEMIZATION_LEVEL:
      case ApplicationScope.ITEMIZATION_LEVEL_PER_QUANTITY:
        this._applicationScope = 'LINE_ITEM_PER_QUANTITY';
        break;
      case ApplicationScope.CART_LEVEL:
      case ApplicationScope.CART_LEVEL_FIXED:
      default:
        this._applicationScope = 'ORDER';
    }
  }
  id() {
    return getSurchargeId(this.surchargeLineItem);
  }

  // We expect the calculator won't care about the inclusionType of surcharge.
  inclusionType() {
    return 'ADDITIVE';
  }
  rate() {
    return this.percentage();
  }
  percentage() {
    if (!isNone(this._percentage)) {
      return String(this._percentage);
    }
    return undefined;
  }
  amount() {
    return this._amount;
  }
  phase() {
    return this.surcharge.calculationPhase || CalculationPhase.SURCHARGE_PHASE;
  }
  appliedTaxes() {
    const result = {};
    this.fees.forEach(feeLineItem => {
      const backingFee = feeLineItem.writeOnlyBackingDetails?.fee;
      result[backingFee.id] = new CalcFee(backingFee);
    });
    return result;
  }
  applicationScope() {
    return this._applicationScope;
  }
  quantity() {
    return this.surchargeLineItem.quantity;
  }
  adjustmentType() {
    return 'ADDITIVE';
  }

  // maxAmount is always null for surcharges.
  maxAmount() {
    return null;
  }
  taxBasis() {
    return this._taxBasis;
  }
}
class CalcModifier {
  constructor(modifierOptionLineItem) {
    this.modifierOptionLineItem = modifierOptionLineItem;
    this.modifierOption = modifierOptionLineItem.writeOnlyBackingDetails?.backingModifierOption;
  }
  id() {
    return this.modifierOption?.id;
  }
  price() {
    return this.modifierOptionLineItem?.price?.amount;
  }
  quantity() {
    // Quantities for modifiers are currently only supported by Restaurants.
    // See: https://prototype.sqprod.co/#/docs/squareup.bills.ModifierOptionLineItem.FeatureDetails.Quantities
    return '1';
  }
}
class CalcItemization {
  constructor(itemization) {
    this.itemization = itemization;
  }

  // Protocol expected by the TaxAndDiscountAdjuster.
  quantity() {
    return String(this.itemization?.quantity || 0);
  }
  price() {
    return this.itemVariationPrice();
  }
  itemType() {
    return 'REGULAR';
  }
  appliedTaxes() {
    const result = {};
    const taxes = this.itemization.get('configuration.selectedOptions.fees');
    if (taxes) {
      taxes.forEach(feeLineItem => {
        const backingFee = feeLineItem.get('writeOnlyBackingDetails.fee');
        result[backingFee.get('id')] = new CalcFee(backingFee);
      });
    }
    return result;
  }
  appliedDiscounts() {
    const result = {};
    const discounts = this.itemization.get('configuration.selectedOptions.discounts');
    if (discounts) {
      discounts.forEach(discountLineItem => {
        const discountId = discountLineItem.get('writeOnlyBackingDetails.discount.id');
        result[discountId] = new CalcDiscount(discountLineItem);
      });
    }
    return result;
  }
  appliedModifiers() {
    const result = {};
    const modifierOptions = this.itemization.get('configuration.selectedOptions.modifierOptions') || [];
    modifierOptions.forEach(modifierOptionLineItem => {
      const calcModifier = new CalcModifier(modifierOptionLineItem);
      result[calcModifier.id()] = calcModifier;
    });
    return result;
  }
  appliedSurcharges() {
    const result = {};
    const surcharges = this.itemization.get('configuration.selectedOptions.surcharges') || [];
    surcharges.forEach(surchargeLineItem => {
      const calcSurcharge = new CalcSurcharge(surchargeLineItem);
      result[calcSurcharge.id()] = calcSurcharge;
    });
    return result;
  }
  appliedCombo() {
    return null;
  }

  // Additional methods we use here:
  itemVariationPrice() {
    return this.itemization.get('configuration.itemVariationPriceMoney.amount') === undefined ? 0 : this.itemization.get('configuration.itemVariationPriceMoney.amount');
  }
  get(key) {
    return this.itemization.get(key);
  }
  set(key, value) {
    return this.itemization.set(key, value);
  }
}
export default class {
  constructor(roundingType) {
    this.roundingType = roundingType;
    this.calculationHelper = new CalculationHelper();
  }
  calculateItemizationBaseAmount(itemization, quantity) {
    const calcItemization = new CalcItemization(itemization);
    return Money.fromCents(this.calculationHelper.itemBaseAmount(calcItemization.price(), quantity ? String(quantity) : calcItemization.quantity(), calcItemization.appliedModifiers()));
  }
  adjustTotals(cart) {
    const featuresService = cart.features;

    // Initialize cart.lineItems if it doesn't exist on the cart object yet.
    if (!cart.get('lineItems')) {
      cart.set('lineItems', Cart.LineItems.create());
    }
    // Gather up the fees so that we can reference them again in the top-level rollup:
    const knownFeeLineItems = {};
    const itemizations = cart.get('lineItems.itemizations') === undefined ? [] : cart.get('lineItems.itemizations');
    itemizations.forEach(itemization => {
      if (!itemization) {
        datadogRum.addAction('INVOICES_UNDEFINED_ITEMIZATION', {});
      }
      return (itemization.get('configuration.selectedOptions.fees') === undefined ? [] : itemization.get('configuration.selectedOptions.fees')).forEach(feeLineItem => {
        const id = feeLineItem.get('writeOnlyBackingDetails.fee.id');
        knownFeeLineItems[id] = feeLineItem;
      });
    });
    const surcharges = cart.lineItems?.surcharges ?? [];
    const nonApportionedServiceCharges = surcharges.filter(surchargeLineItem => {
      return !isApportionedSurcharge(surchargeLineItem);
    });
    // taxes for apportioned service charges are included when gather taxes for itemizations.
    nonApportionedServiceCharges.forEach(surchargeLineItem => surchargeLineItem.fees.forEach(feeLineItem => {
      const id = feeLineItem.writeOnlyBackingDetails?.fee?.id;
      knownFeeLineItems[id] = feeLineItem;
    }));
    const calcItemizations = itemizations.map(itemization => new CalcItemization(itemization));
    // apportioned service charges should not be included during TaxAndDiscountAdjuster initialization
    const calcSurcharges = nonApportionedServiceCharges.map(surchargeLineItem => new CalcSurcharge(surchargeLineItem));
    const adjusterConfig = new CalcAdjusterConfig();
    const clock = new CalcClock();
    const taxAndDiscountAdjuster = new TaxAndDiscountAdjuster(calcItemizations, calcSurcharges, this.roundingType, adjusterConfig, clock);
    calcItemizations.forEach(calcItemization => {
      const adjustedItem = taxAndDiscountAdjuster.getAdjustedItemFor(calcItemization);
      const modifierOptions = calcItemization.get('modifierOptions');
      if (modifierOptions) {
        modifierOptions.forEach(modifierOption => {
          const calcModifier = new CalcModifier(modifierOption);
          modifierOption.set('amounts', Itemization.ModifierAmounts.create({
            modifierOptionMoney: modifierOption.get('price'),
            modifierOptionTimesQuantityMoney: Money.fromCents(this.calculationHelper.totalModifierPrice(calcModifier.price(), calcModifier.quantity(), calcItemization.quantity()))
          }));
        });
      }
      calcItemization.set('amounts', Itemization.Amounts.create({
        itemVariationPriceMoney: Money.fromCents(calcItemization.itemVariationPrice()),
        itemVariationPriceTimesQuantityMoney: Money.fromCents(this.calculationHelper.itemPriceTimesItemQuantity(calcItemization.price(), calcItemization.quantity())),
        discountMoney: Money.fromCents(adjustedItem.getTotalCollectedForAllDiscounts()),
        taxMoney: Money.fromCents(adjustedItem.getTotalCollectedForAllTaxes()),
        surchargeMoney: Money.fromCents(adjustedItem.getTotalCollectedForAllAppliedSurcharges()),
        totalMoney: Money.fromCents(adjustedItem.getAdjustedTotal()),
        grossSalesMoney: Money.fromCents(adjustedItem.getBaseAmount())
      }));
      const adjustmentAmounts = adjustedItem.getTotalCollectedPerAdjustment();
      const discounts = calcItemization.get('configuration.selectedOptions.discounts');
      if (discounts) {
        discounts.forEach(discount => {
          const id = discount.get('writeOnlyBackingDetails.discount.id');
          discount.set('amounts', DiscountLineItem.Amounts.create({
            appliedMoney: Money.fromCents(adjustmentAmounts[id])
          }));
        });
      }
      const taxes = calcItemization.get('configuration.selectedOptions.fees');
      if (taxes) {
        taxes.forEach(fee => {
          const id = fee.get('writeOnlyBackingDetails.fee.id');
          fee.set('amounts', FeeLineItem.Amounts.create({
            appliedMoney: Money.fromCents(adjustmentAmounts[id])
          }));
        });
      }
    });
    const collectedPerTax = taxAndDiscountAdjuster.getCollectedAmountPerTax();

    // TODO(ryanly): Remove when this when afterApplicationTotalMoney and appliedToMoney are stable
    const canInvoicesDashboardNewTaxAmountFields = featuresService.isEnabled('invoices/dashboard_new_tax_amount_fields');
    cart.set('lineItems.fees.[]', taxAndDiscountAdjuster.getAppliedTaxAdjustments().map(taxAdjustment => {
      const feeLineItemAmountsObject = {
        appliedMoney: Money.fromCents(collectedPerTax[taxAdjustment.id()])
      };
      if (canInvoicesDashboardNewTaxAmountFields) {
        feeLineItemAmountsObject.afterApplicationTotalMoney = Money.fromCents(calculateAfterApplicationItemizationsTotalAmount({
          calcSurcharges,
          calcItemizations,
          taxAndDiscountAdjuster,
          taxAdjustment
        }));
        feeLineItemAmountsObject.appliedToMoney = Money.fromCents(calculateAppliedToAmount({
          calcItemizations,
          calcSurcharges,
          taxAndDiscountAdjuster,
          taxAdjustment
        }));
      }
      return FeeLineItem.create({
        amounts: FeeLineItem.Amounts.create(feeLineItemAmountsObject),
        feeLineItemTokenPair: TokenPair.create({
          clientToken: UUID.create().toString()
        }),
        writeOnlyBackingDetails: FeeLineItem.BackingDetails.create({
          fee: knownFeeLineItems[taxAdjustment.id()].get('writeOnlyBackingDetails.fee')
        })
      });
    }));
    const collectedPerDiscount = taxAndDiscountAdjuster.getCollectedAmountPerDiscount();
    cart.get('lineItems.discounts').forEach(discount => {
      const id = discount.get('writeOnlyBackingDetails.discount.id');
      discount.set('amounts', DiscountLineItem.Amounts.create({
        appliedMoney: Money.fromCents(collectedPerDiscount[id])
      }));
    });
    const collectedPerSurcharge = taxAndDiscountAdjuster.getCollectedAmountPerSurcharge();
    const collectedPerAppliedSurcharge = taxAndDiscountAdjuster.getCollectedAmountPerAppliedSurcharge();
    cart.get('lineItems.surcharges').forEach(surcharge => {
      const id = getSurchargeId(surcharge);
      // we do not calculate taxes for apportioned surcharges as it is included in applicable line items
      if (isApportionedSurcharge(surcharge)) {
        surcharge.set('amounts', SurchargeLineItem.Amounts.create({
          appliedMoney: Money.fromCents(collectedPerAppliedSurcharge[id])
        }));
        return;
      }
      surcharge.set('amounts', SurchargeLineItem.Amounts.create({
        appliedMoney: Money.fromCents(collectedPerSurcharge[id])
      }));
      const relatedCalcSurcharge = calcSurcharges.find(calcSurcharge => calcSurcharge.id() === id);
      const surchargeAdjustedItem = taxAndDiscountAdjuster.getSurchargeAdjustedItemFor(relatedCalcSurcharge);
      const adjustmentAmounts = surchargeAdjustedItem.getTotalCollectedPerAdjustment();
      surcharge.fees.forEach(fee => {
        const id = fee.writeOnlyBackingDetails.fee.id;
        fee.set('amounts', FeeLineItem.Amounts.create({
          appliedMoney: Money.fromCents(adjustmentAmounts[id])
        }));
      });
    });
    cart.set('amounts', Cart.Amounts.create({
      totalMoney: Money.fromCents(taxAndDiscountAdjuster.getTotal()),
      taxMoney: Money.fromCents(taxAndDiscountAdjuster.getTotalCollectedForAllTaxes()),
      discountMoney: Money.fromCents(taxAndDiscountAdjuster.getTotalCollectedForAllDiscounts()),
      tipMoney: Money.zero(),
      surchargeMoney: Money.fromCents(taxAndDiscountAdjuster.getTotalCollectedForAllSurcharges() + taxAndDiscountAdjuster.getTotalCollectedForAllAppliedSurcharges())
    }));
  }
}